import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, Navigate, useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import type { CompanyBranding, SessionInfo } from "@techplanner/shared/core";
import { useAuth } from "../auth";
import { Alert, Button, Field, Skeleton, useConfirm } from "../components/ui";
import { api, ApiError, errorMessage } from "../lib/api";
import { unsentLogoutMessage, useClosures } from "../lib/closures";
import {
  applyCompanyApp,
  applyCompanyColor,
  forgetCompany,
  normalizeCompanyCode,
  rememberBranding,
  rememberCompanyCode,
  storedBranding,
  storedCompanyCode
} from "../lib/company";

const LAST_USERNAME_KEY = "tp:last-username";

function fetchBranding(code: string) {
  return api<CompanyBranding>(`/public/companies/${encodeURIComponent(code)}`);
}

/** Logo de la société, ou à défaut son initiale sur sa couleur. */
export function CompanyMark({ branding, size = 72 }: { branding: CompanyBranding; size?: number }) {
  const [broken, setBroken] = useState(false);
  if (branding.logoUrl && !broken) {
    return (
      <img className="company-logo" src={branding.logoUrl} alt="" width={size} height={size} onError={() => setBroken(true)} />
    );
  }
  return (
    <span className="company-monogram" style={{ width: size, height: size, fontSize: size * 0.44 }} aria-hidden="true">
      {branding.name.trim().charAt(0).toUpperCase()}
    </span>
  );
}

/** Lien d'invitation /s/<code>?u=<identifiant> : retient la société sur le téléphone puis ouvre la connexion. */
export function InvitationPage() {
  const { code: raw = "" } = useParams();
  const [params] = useSearchParams();
  const { user, company, signOut } = useAuth();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const unsent = useClosures().items.filter((item) => item.state !== "refused").length;
  const started = useRef(false);
  const code = normalizeCompanyCode(decodeURIComponent(raw));

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void (async () => {
      if (code) {
        // Lien d'une autre société que celle de la session : on repart de la connexion,
        // après confirmation si des bons attendent encore d'être envoyés depuis ce téléphone.
        if (user && company && company.code !== code) {
          const ok =
            unsent === 0 ||
            (await confirm({
              title: unsent > 1 ? "Bons en attente d'envoi" : "Bon en attente d'envoi",
              message: unsentLogoutMessage(unsent),
              confirmLabel: "Se déconnecter",
              danger: true
            }));
          if (!ok) {
            navigate("/", { replace: true });
            return;
          }
          await signOut();
        }
        rememberCompanyCode(code);
        const username = params.get("u")?.trim().toLowerCase();
        if (username) localStorage.setItem(LAST_USERNAME_KEY, username);
      }
      navigate(user && company?.code === code ? "/" : "/login", { replace: true });
    })();
    // Une seule fois, à l'ouverture du lien.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <main className="login">
      <Skeleton count={3} />
    </main>
  );
}

export function LoginPage() {
  const { user, signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? "/";
  const [code, setCode] = useState<string | null>(() => storedCompanyCode());

  const branding = useQuery({
    queryKey: ["branding", code],
    queryFn: () => fetchBranding(code!),
    enabled: Boolean(code),
    initialData: () => storedBranding() ?? undefined,
    staleTime: 0,
    retry: false
  });

  const notFound = branding.error instanceof ApiError && branding.error.status === 404;
  useEffect(() => {
    if (branding.data) {
      rememberBranding(branding.data);
      applyCompanyColor(branding.data.primaryColor);
      applyCompanyApp(branding.data);
    }
  }, [branding.data]);
  useEffect(() => {
    if (notFound) {
      forgetCompany();
      applyCompanyColor(null);
      applyCompanyApp(null);
      setCode(null);
    }
  }, [notFound]);

  if (user) return <Navigate to={from} replace />;

  function changeCompany() {
    forgetCompany();
    applyCompanyColor(null);
    applyCompanyApp(null);
    setCode(null);
  }

  if (!code) {
    return (
      <CompanyCodeStep
        unknownCode={notFound}
        onFound={(found) => {
          rememberCompanyCode(found.code);
          rememberBranding(found);
          setCode(found.code);
        }}
      />
    );
  }

  return (
    <CredentialsStep
      code={code}
      branding={branding.data ?? null}
      onChangeCompany={changeCompany}
      onSignedIn={(session) => {
        signIn(session);
        navigate(from, { replace: true });
      }}
    />
  );
}

function CompanyCodeStep({ unknownCode, onFound }: { unknownCode: boolean; onFound: (b: CompanyBranding) => void }) {
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(unknownCode ? "Ce code société n'existe plus. Saisissez-le à nouveau." : null);
  const [loading, setLoading] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (loading) return;
    const code = normalizeCompanyCode(value);
    if (!code) {
      setError("Code société invalide : lettres minuscules, chiffres et tirets.");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      onFound(await fetchBranding(code));
    } catch (err) {
      setError(err instanceof ApiError && err.status === 404 ? "Aucune société ne porte ce code." : errorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="login">
      <img className="login-logo" src="/icons/icon.svg" alt="" width={72} height={72} />
      <h1>TechPlanner</h1>
      <p className="text-2" style={{ marginTop: 6, marginBottom: 28 }}>
        Ouvrez le lien d'invitation envoyé par votre responsable, ou saisissez le code de votre société.
      </p>
      <form className="form" onSubmit={submit} noValidate>
        {error && <Alert kind="error">{error}</Alert>}
        <Field label="Code société" htmlFor="company-code">
          <input
            id="company-code"
            className="input"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            enterKeyHint="go"
            placeholder="ex. dupont-chauffage"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            required
          />
        </Field>
        <Button type="submit" variant="primary" size="large" block loading={loading} disabled={!value.trim()}>
          Continuer
        </Button>
      </form>
      <div className="signup-cta">
        <p className="small text-2">Vous dirigez une équipe de techniciens ?</p>
        <Link to="/inscription" className="btn block">
          Créer le compte de ma société
        </Link>
      </div>
    </main>
  );
}

function CredentialsStep({
  code,
  branding,
  onChangeCompany,
  onSignedIn
}: {
  code: string;
  branding: CompanyBranding | null;
  onChangeCompany: () => void;
  onSignedIn: (session: SessionInfo) => void;
}) {
  const [username, setUsername] = useState(() => localStorage.getItem(LAST_USERNAME_KEY) ?? "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (loading) return;
    setError(null);
    setLoading(true);
    try {
      const res = await api<SessionInfo>("/auth/login", { method: "POST", body: { company: code, username, password } });
      localStorage.setItem(LAST_USERNAME_KEY, username.trim().toLowerCase());
      onSignedIn(res);
    } catch (err) {
      setError(errorMessage(err));
      setPassword("");
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="login">
      {branding ? <CompanyMark branding={branding} /> : <img className="login-logo" src="/icons/icon.svg" alt="" width={72} height={72} />}
      <h1>{branding?.name ?? "TechPlanner"}</h1>
      <p className="text-2" style={{ marginTop: 6, marginBottom: 28 }}>
        Planning et interventions de l'équipe.
      </p>
      <form className="form" onSubmit={submit} noValidate>
        {error && <Alert kind="error">{error}</Alert>}
        <Field label="Identifiant" htmlFor="username">
          <input
            id="username"
            className="input"
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            enterKeyHint="next"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
          />
        </Field>
        <Field label="Mot de passe" htmlFor="password">
          <div style={{ position: "relative" }}>
            <input
              id="password"
              className="input"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              enterKeyHint="go"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              style={{ paddingRight: 96 }}
            />
            <button
              type="button"
              className="btn ghost small"
              style={{ position: "absolute", right: 4, top: 6 }}
              onClick={() => setShowPassword((v) => !v)}
            >
              {showPassword ? "Masquer" : "Afficher"}
            </button>
          </div>
        </Field>
        <Button type="submit" variant="primary" size="large" block loading={loading} disabled={!username || !password}>
          Se connecter
        </Button>
        <p className="small muted" style={{ textAlign: "center" }}>
          <Link to="/mot-de-passe-oublie">Mot de passe oublié ?</Link>
        </p>
        <p className="small" style={{ textAlign: "center" }}>
          <button type="button" className="link-button" onClick={onChangeCompany}>
            Pas votre société ? Changer de société
          </button>
        </p>
      </form>
      <p className="small muted login-powered">avec TechPlanner</p>
    </main>
  );
}
